import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useUILocale } from '../hooks/useUILocale';
import { useHreflangHead } from '../hooks/useHreflangHead';
import { withLocale } from '../locale';

const SupportPage: React.FC = () => {
  const uiLocale = useUILocale();
  const href = (p: string) => withLocale(uiLocale, p);
  useHreflangHead(uiLocale, '/support');
  return (
    <Layout title="Support">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="bg-primary-600 px-6 py-8 text-white">
            <h1 className="text-2xl font-bold">Support & About</h1>
            <p className="text-primary-100 mt-1">Everything you need to know about StoryChain</p>
          </div>

          <div className="p-6 space-y-6">
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">What is StoryChain?</h2>
              <p className="text-gray-600 text-sm">
                StoryChain is a collaborative storytelling game where friends write stories together,
                one sentence at a time. Each participant sees only the previous sentence and adds their
                own continuation. When all players have contributed, AI generates a coherent final story
                from all the fragments.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">How to Play</h2>
              <ol className="text-gray-600 text-sm space-y-2">
                <li className="flex gap-2"><span className="font-medium">1.</span><span>Create a new story and pick a genre — Comedy, Horror, Romance, Sci-Fi, Fantasy, or Random (the AI picks for you).</span></li>
                <li className="flex gap-2"><span className="font-medium">2.</span><span>Write your opening sentence to start the story.</span></li>
                <li className="flex gap-2"><span className="font-medium">3.</span><span>Share the 6-character invite code or the link with friends.</span></li>
                <li className="flex gap-2"><span className="font-medium">4.</span><span>Each player sees only the previous sentence and writes the next one.</span></li>
                <li className="flex gap-2"><span className="font-medium">5.</span><span>When the last player has contributed, AI weaves all the fragments into a complete story (and a cover image, if available).</span></li>
                <li className="flex gap-2"><span className="font-medium">6.</span><span>Read it together, then choose to keep it Public (so it shows up in Explore) or Unlisted (link-only).</span></li>
              </ol>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Frequently Asked Questions</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-800">How many people can write a story together?</h3>
                  <p className="text-gray-600 text-sm mt-1">From 2 up to 20. The creator picks the size when starting the story.</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-800">Do I need an account to join?</h3>
                  <p className="text-gray-600 text-sm mt-1">No — you can join an existing story by code without signing up. Anonymous participants pick a display name and can keep contributing on the same device. To <em>create</em> stories you do need an account (email + one-time code, Sign in with Apple, or Sign in with Google).</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-800">Can I see what others wrote before the story is complete?</h3>
                  <p className="text-gray-600 text-sm mt-1">No, that's the fun part! You only see the sentence immediately before yours. The full story is revealed only after everyone has contributed.</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-800">How does the AI generate the final story?</h3>
                  <p className="text-gray-600 text-sm mt-1">The AI reads all the individual contributions and weaves them into a coherent narrative that keeps each person's ideas intact while matching the chosen genre's tone.</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-800">Is there a character limit for each contribution?</h3>
                  <p className="text-gray-600 text-sm mt-1">Yes, each contribution can be up to 280 characters to keep the game moving and encourage concise writing.</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-800">Why does creating a story cost coins?</h3>
                  <p className="text-gray-600 text-sm mt-1">Coins cover the AI cost of generating the final story and cover image. Every account gets a daily free coin allowance that refills at 00:00 UTC — enough for several stories per day. The cost of each action is shown before you confirm it. If a generation fails on our side, the coins for that action are refunded automatically.</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-800">Who can see my finished story?</h3>
                  <p className="text-gray-600 text-sm mt-1">Stories start as Public — anyone can find them in Explore. Any participant can switch a story to Unlisted, which removes it from Explore for everyone immediately; only people with the direct link can open it after that. The "Hide" action in your personal list is separate — it only hides the story from your own view, not from other participants or Explore.</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-800">Can I delete my account?</h3>
                  <p className="text-gray-600 text-sm mt-1">Yes, you can delete your account from the Profile tab in the app, or visit our <Link to={href('/privacy/remove')} className="text-primary-600 hover:underline">account removal page</Link>. Deletion permanently removes your stories, contributions, and balance.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Contact Us</h2>
              <p className="text-gray-600 text-sm mb-3">
                Have questions, feedback, or need help? We'd love to hear from you!
              </p>
              <p className="text-gray-600 text-sm">
                Email:{' '}
                <a href="mailto:support@storychain.fun" className="text-primary-600 hover:underline">
                  support@storychain.fun
                </a>
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Legal</h2>
              <div className="flex gap-4">
                <Link to={href('/privacy')} className="text-primary-600 hover:underline text-sm">Privacy Policy</Link>
                <Link to={href('/terms')} className="text-primary-600 hover:underline text-sm">Terms of Service</Link>
              </div>
            </section>
          </div>
        </div>

        <p className="text-center text-sm text-gray-400 mt-6">
          StoryChain &copy; {new Date().getFullYear()}
        </p>
      </div>
    </Layout>
  );
};

export default SupportPage;
